import { Linking, Platform } from 'react-native';

import { getFlagSync } from '@fhs-legacy/frontend/src/utils/launchdarkly/flag-cache';

import { PendingOAuthReturn, TryOAuthRedirectAfterLoginOptions } from './types';

const STORAGE_KEY = 'fhs:oauth_pending_return:v1';

/** Redirect URI schemes permitted for OAuth. Add new app schemes here after review. */
const ALLOWED_REDIRECT_SCHEMES = new Set(['http:', 'https:', 'cursor:']);

/** Normalizes LaunchDarkly JSON flag value into redirect allowlist entries. */
export function parseOAuthRedirectAllowlist(flagValue: unknown): string[] {
  if (!Array.isArray(flagValue)) {
    return [];
  }
  return flagValue
    .filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '')
    .map(entry => entry.trim());
}

/** Allowlist from LaunchDarkly (`oauth-redirect-allowed-origins`). */
export function getOAuthRedirectAllowlist(): string[] {
  return parseOAuthRedirectAllowlist(getFlagSync('oauth-redirect-allowed-origins'));
}

/** First value when Expo Router passes `string | string[]` for query params. */
export function firstOAuthSearchParam(value: string | string[] | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return Array.isArray(value) ? value[0] : value;
}

function redirectUriMatchesAllowlistEntry(redirectUrl: URL, entry: string): boolean {
  if (entry.includes('://')) {
    let entryUrl: URL;
    try {
      entryUrl = new URL(entry);
    } catch {
      return false;
    }
    if (redirectUrl.protocol !== entryUrl.protocol || redirectUrl.hostname !== entryUrl.hostname) {
      return false;
    }
    const entryPath = entryUrl.pathname.replace(/\/$/, '') || '/';
    if (entryPath !== '/' && !redirectUrl.pathname.startsWith(entryPath)) {
      return false;
    }
    return true;
  }
  return redirectUrl.hostname === entry || redirectUrl.hostname.endsWith(`.${entry}`);
}

export function isRedirectUriHostAllowed(
  redirectUri: string,
  allowlist: string[] = getOAuthRedirectAllowlist()
): boolean {
  let redirectUrl: URL;
  try {
    redirectUrl = new URL(redirectUri);
  } catch {
    return false;
  }
  if (!ALLOWED_REDIRECT_SCHEMES.has(redirectUrl.protocol)) {
    return false;
  }
  if (allowlist.length === 0) {
    return false;
  }

  const isHttpOrHttps = redirectUrl.protocol === 'http:' || redirectUrl.protocol === 'https:';
  if (isHttpOrHttps) {
    return allowlist.some(entry => redirectUriMatchesAllowlistEntry(redirectUrl, entry));
  }

  // Custom schemes (e.g. cursor://) require an explicit full-URI allowlist entry.
  return allowlist.some(
    entry => entry.includes('://') && redirectUriMatchesAllowlistEntry(redirectUrl, entry)
  );
}

function appendStateToRedirectUri(redirectUri: string, state?: string): string {
  if (state === undefined || state === '') {
    return redirectUri;
  }
  const u = new URL(redirectUri);
  u.searchParams.set('state', state);
  return u.toString();
}

function appendAuthorizationCodeToRedirectUrl(
  redirectUri: string,
  authorizationCode: string
): string {
  const u = new URL(redirectUri);
  u.searchParams.set('code', authorizationCode);
  return u.toString();
}

function readSessionStorageItem(key: string): string | null {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  return window.sessionStorage.getItem(key);
}

function removeSessionStorageItem(key: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  window.sessionStorage.removeItem(key);
}

function setSessionStorageItem(key: string, value: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  window.sessionStorage.setItem(key, value);
}

/** OAuth PKCE `state` for Auth V2 mutations (`oauthState`), from sessionStorage only. */
export function resolveOAuthStateForMutation(): string | undefined {
  const raw = readSessionStorageItem(STORAGE_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as PendingOAuthReturn;
    const state = parsed?.state;
    return state !== undefined && state !== '' ? state : undefined;
  } catch {
    return undefined;
  }
}

export function setPendingOAuthReturnFromParams(
  redirectUriRaw: string | undefined,
  stateRaw?: string | undefined,
  allowlist: string[] = getOAuthRedirectAllowlist()
): boolean {
  if (!redirectUriRaw) {
    return false;
  }
  let redirectUri = redirectUriRaw;
  try {
    redirectUri = decodeURIComponent(redirectUriRaw);
  } catch {
    redirectUri = redirectUriRaw;
  }
  if (!isRedirectUriHostAllowed(redirectUri, allowlist)) {
    return false;
  }
  const payload: PendingOAuthReturn = {
    redirectUri,
    ...(stateRaw !== undefined && stateRaw !== '' ? { state: stateRaw } : {}),
  };
  setSessionStorageItem(STORAGE_KEY, JSON.stringify(payload));
  return true;
}

function performOAuthReturnNavigation(
  redirectUri: string,
  state: string | undefined,
  authorizationCode: string,
  allowlist: string[]
): boolean {
  if (!isRedirectUriHostAllowed(redirectUri, allowlist)) {
    return false;
  }
  let target = appendStateToRedirectUri(redirectUri, state);
  target = appendAuthorizationCodeToRedirectUrl(target, authorizationCode);
  if (Platform.OS === 'web') {
    window.location.assign(target);
    return true;
  }
  void Linking.openURL(target);
  return true;
}

function tryConsumeAndRedirectOAuthPending(
  authorizationCode: string,
  allowlist: string[]
): boolean {
  const raw = readSessionStorageItem(STORAGE_KEY);
  if (!raw) {
    return false;
  }
  let parsed: PendingOAuthReturn;
  try {
    parsed = JSON.parse(raw) as PendingOAuthReturn;
  } catch {
    removeSessionStorageItem(STORAGE_KEY);
    return false;
  }
  if (!parsed?.redirectUri || !isRedirectUriHostAllowed(parsed.redirectUri, allowlist)) {
    removeSessionStorageItem(STORAGE_KEY);
    return false;
  }
  removeSessionStorageItem(STORAGE_KEY);
  return performOAuthReturnNavigation(
    parsed.redirectUri,
    parsed.state,
    authorizationCode,
    allowlist
  );
}

/**
 * After login/sign-up: redirect to `redirect_uri` with PKCE `code` and `state`.
 * Returns true when a redirect was initiated.
 */
export function tryOAuthRedirectAfterLogin(options: TryOAuthRedirectAfterLoginOptions): boolean {
  const allowlist = options.allowlist ?? getOAuthRedirectAllowlist();
  if (tryConsumeAndRedirectOAuthPending(options.authorizationCode, allowlist)) {
    return true;
  }
  const redirectUri = firstOAuthSearchParam(options.redirectUriSearchParam);
  if (!redirectUri) {
    return false;
  }
  const state = firstOAuthSearchParam(options.stateSearchParam);
  return performOAuthReturnNavigation(redirectUri, state, options.authorizationCode, allowlist);
}

/** Persist `redirect_uri` / `state` from the current URL (call from `/auth` layout). */
export function persistOAuthRedirectFromSearchParams(
  redirectUriRaw: string | string[] | undefined,
  stateRaw: string | string[] | undefined,
  allowlist: string[] = getOAuthRedirectAllowlist()
): boolean {
  const redirectUri = firstOAuthSearchParam(redirectUriRaw);
  if (!redirectUri) {
    return false;
  }
  return setPendingOAuthReturnFromParams(redirectUri, firstOAuthSearchParam(stateRaw), allowlist);
}
